import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  updateCampaignTimes,
  UpdateCampaignTimesRequest,
  VotingPeriod,
} from "utils/apiService/PinkSale";

interface UpdateCampaignTimesModalProps {
  isOpen: boolean;
  onClose: () => void;
  campaign: VotingPeriod | null;
  onSuccess?: () => void;
}

export default function UpdateCampaignTimesModal({
  isOpen,
  onClose,
  campaign,
  onSuccess,
}: UpdateCampaignTimesModalProps) {
  const toast = useToast();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<UpdateCampaignTimesRequest>({
    defaultValues: {
      startTime: "",
      endTime: "",
    },
  });

  // Reset form when modal opens/closes or campaign changes
  useEffect(() => {
    if (isOpen && campaign) {
      reset({
        startTime: campaign.startTime || "",
        endTime: campaign.endTime || "",
      });
    } else if (!isOpen) {
      reset({
        startTime: "",
        endTime: "",
      });
    }
  }, [isOpen, campaign, reset]);

  const onSubmit = async (data: UpdateCampaignTimesRequest) => {
    if (!campaign) return;

    try {
      // Only send fields that have values
      const finalPayload: UpdateCampaignTimesRequest = {};
      if (data.startTime) {
        finalPayload.startTime = data.startTime;
      }
      if (data.endTime) {
        finalPayload.endTime = data.endTime;
      }

      await updateCampaignTimes(campaign._id, finalPayload);

      toast({
        title: "Success",
        description: "Campaign times updated successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      onSuccess?.();
      onClose();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.message || "Failed to update campaign times",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleClose = () => {
    reset({
      startTime: "",
      endTime: "",
    });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Update Campaign Times</ModalHeader>
        <ModalCloseButton />
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalBody pb={6}>
            <VStack spacing={4} align="stretch">
              <FormControl isInvalid={Boolean(errors.startTime)}>
                <FormLabel>Start Time (UTC ISO format)</FormLabel>
                <Input
                  type="text"
                  placeholder="2024-01-01T00:00:00.000Z"
                  {...register("startTime", {
                    validate: (value) => {
                      if (!value) return true; // Optional field
                      const date = new Date(value);
                      if (isNaN(date.getTime())) {
                        return "Invalid ISO date format";
                      }
                      // Check if it matches ISO 8601 format pattern
                      if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
                        return "Must be a valid ISO 8601 date string (e.g., 2024-01-01T00:00:00.000Z)";
                      }
                      return true;
                    },
                  })}
                />
                <FormErrorMessage>{errors.startTime?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={Boolean(errors.endTime)}>
                <FormLabel>End Time (UTC ISO format)</FormLabel>
                <Input
                  type="text"
                  placeholder="2024-01-01T00:00:00.000Z"
                  {...register("endTime", {
                    validate: (value, formValues) => {
                      if (!value) return true; // Optional field
                      const date = new Date(value);
                      if (isNaN(date.getTime())) {
                        return "Invalid ISO date format";
                      }
                      // Check if it matches ISO 8601 format pattern
                      if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
                        return "Must be a valid ISO 8601 date string (e.g., 2024-01-01T00:00:00.000Z)";
                      }
                      // Validate that end time is after start time if both are provided
                      if (formValues.startTime) {
                        const startDate = new Date(formValues.startTime);
                        if (date <= startDate) {
                          return "End time must be after start time";
                        }
                      }
                      return true;
                    },
                  })}
                />
                <FormErrorMessage>{errors.endTime?.message}</FormErrorMessage>
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button
              colorScheme="blue"
              mr={3}
              type="submit"
              isLoading={isSubmitting}
            >
              Update
            </Button>
            <Button onClick={handleClose} isDisabled={isSubmitting}>
              Cancel
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}
